import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Chip, Grid } from "@mui/material";
import { BookmarkBorder, Visibility } from "@mui/icons-material";

// Argon Dashboard 2 MUI components
import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";
import Button from "themeComponents/Button";
import DashboardLayout from "themeComponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "themeComponents/Navbars/DashboardNavbar";
import Footer from "themeComponents/Footer";
import Loader from "components/loader";
import { AppContext } from "context/appContext";
import { COMPCHECK_URL, courseApi } from "configs/api";
import { Course } from "slices/courseSlice";
import PageHeader from "./components/PageHeader";
import EmptyStateCard from "./components/EmptyStateCard";
import CourseProgressBar from "./components/CourseProgressBar";
import CourseCardShell from "./components/CourseCardShell";
import { formatCoursePrice } from "./utils/courseFormatters";

type SavedCourseEntry = {
  id: string;
  courseID: string;
  courseProgress?: number;
  enrollmentStatus?: string;
  course: Course | null;
};

function CourseLibrary(): React.JSX.Element {
  const navigate = useNavigate();
  const { showAlert } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [savedCourses, setSavedCourses] = useState<SavedCourseEntry[]>([]);

  useEffect(() => {
    fetchSavedCourses();
  }, []);

  const fetchSavedCourses = async () => {
    setLoading(true);
    try {
      const response = await courseApi.get("/api/courses/saved");
      console.log("Saved courses response:", response);
      if (response.data) {
        const savedData = Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
          ? response.data
          : [];
        const courses = savedData.filter((saved: SavedCourseEntry) => saved.course);
        setSavedCourses(courses);
      } else {
        setSavedCourses([]);
      }
    } catch (error: any) {
      console.error("Error fetching saved courses:", error);
      showAlert(error.response?.data?.message || "Failed to fetch saved courses", "error", "Error");
      setSavedCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUnsave = async (courseID: string) => {
    try {
      await courseApi.delete(`/api/courses/save/${courseID}`);
      setSavedCourses(savedCourses.filter((entry) => entry.courseID !== courseID));
      showAlert("Course removed from library", "success", "Success");
    } catch (error: any) {
      showAlert(error.response?.data?.message || "Failed to unsave course", "error", "Error");
    }
  };

  if (loading) {
    return <Loader loading={true} />;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <PageHeader
          title="My Course Library"
          subtitle="Your saved courses for easy access"
        />

        {savedCourses.length === 0 ? (
          <EmptyStateCard
            title="No saved courses yet"
            description="Save courses you are interested in to access them quickly from here."
            actionLabel="Browse Courses"
            onAction={() => navigate("/courses")}
          />
        ) : (
          <Grid container spacing={3}>
            {savedCourses.map((entry) => {
              const course = entry.course;
              if (!course) return null;

              return (
              <Grid item xs={12} md={6} lg={4} key={course.id}>
                <CourseCardShell
                  hoverable={false}
                  bodyPadding={0}
                  bodyFlex={0}
                  cardSx={{ cursor: "default" }}
                >
                  {course.thumbnailURL && (
                    <Box
                      component="img"
                      src={`${COMPCHECK_URL}/api/${course.thumbnailURL}`}
                      alt={course.title}
                      sx={{
                        width: "100%",
                        height: 200,
                        objectFit: "cover",
                      }}
                    />
                  )}
                  <Box p={2} flex={1} display="flex" flexDirection="column" {...({} as any)}>
                    <Typography variant="h6" mb={1}>
                      {course.title}
                    </Typography>
                    {course.description && (
                      <Typography variant="body2" color="text" mb={2} sx={{ flexGrow: 1 }}>
                        {course.description.substring(0, 100)}...
                      </Typography>
                    )}
                    <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                      {course.isFree ? (
                        <Chip label="Free" color="success" size="small" />
                      ) : (
                        <Chip label={formatCoursePrice(course.price, course.isFree)} color="primary" size="small" />
                      )}
                      {course.status === "published" && <Chip label="Published" color="info" size="small" />}
                      {course.rating && (
                        <Chip label={`⭐ ${course.rating.toFixed(1)}`} color="warning" size="small" />
                      )}
                      {entry.enrollmentStatus && (
                        <Chip label={entry.enrollmentStatus} color={entry.enrollmentStatus === "completed" ? "success" : "default"} size="small" />
                      )}
                    </Box>
                    <Box mb={2}>
                      <CourseProgressBar label="Course completion" value={Number(entry.courseProgress || 0)} />
                    </Box>
                    <Box display="flex" gap={1}>
                      <Button
                        variant="gradient"
                        color="info"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/courses/${course.id}`);
                        }}
                      >
                        <Visibility sx={{ mr: 0.5, fontSize: 16 }} /> View
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnsave(course.id);
                        }}
                      >
                        <BookmarkBorder sx={{ mr: 0.5, fontSize: 16 }} /> Remove
                      </Button>
                    </Box>
                  </Box>
                </CourseCardShell>
              </Grid>
            );})}
          </Grid>
        )}
      </Box>
      <Footer />
    </DashboardLayout>
  );
}

export default CourseLibrary;

